import { format } from "date-fns";
import qs from "qs";
import httpClient from "../config/HttpClient";
import EmailFilterModel from "../email/EmailFilterModel";
import { InvoiceFilterModel } from "../invoice/InvoiceFilterModel";

export const STATISTICS_URL = "/statistics";

export interface UserInvoiceStatistics {
  pvm: number | null;
  price: number | null;
  discount: number | null;
  sum: number | null;
  count: number;
  createdByName: string | null;
}

export interface ClientCount {
  count: number;
  state: string;
}

export interface ActiveClientCount {
  count: number;
  month: string;
}

export interface MonthlyIncome {
  date: string;
  totalPrice: number;
  totalPvm: number;
  sum: number;
}

export interface EmailStatistics {
  count: number;
  createdByName: string;
}

export type StatisticsEndpoints = {
  users: UserInvoiceStatistics[];
  'clients/counts': ClientCount[];
  'clients/active-counts': ActiveClientCount[];
  'monthly-income': MonthlyIncome[];
  'email-statistics': EmailStatistics[];
};

export class StatisticsModel {
  private static serializeParams<T extends Record<string, any>>(params: T) {
    return qs.stringify(params, {
      serializeDate: (date: Date) => format(date, "yyyy-MM-dd"),
    });
  }

  private static async get<K extends keyof StatisticsEndpoints>(
    endpoint: K,
    params?: Record<string, any>
  ): Promise<StatisticsEndpoints[K]> {
    const response = await httpClient.get<StatisticsEndpoints[K]>(`${STATISTICS_URL}/${endpoint}`, {
      params,
      ...(params && { paramsSerializer: this.serializeParams }),
    });
    return response.data;
  }

  static async getUserInvoiceStatistics(filter?: InvoiceFilterModel): Promise<UserInvoiceStatistics[]> {
    return this.get('users', filter);
  }

  static async getClientCounts(): Promise<ClientCount[]> {
    return this.get('clients/counts');
  }

  static async getActiveClientStats(): Promise<ActiveClientCount[]> {
    return this.get('clients/active-counts');
  }

  static async getMonthlyIncome(): Promise<MonthlyIncome[]> {
    return this.get('monthly-income');
  }

  static async getEmailStatistics(filter: EmailFilterModel): Promise<EmailStatistics[]> {
    return this.get('email-statistics', filter);
  }

  // Utility methods for data processing
  static calculateTotalIncome(monthlyIncomes: MonthlyIncome[]): number {
    return monthlyIncomes.reduce((total, income) => total + income.sum, 0);
  }

  static calculateTotalClients(clientCounts: ClientCount[]): number {
    return clientCounts.reduce((total, count) => total + count.count, 0);
  }

  static calculateAverageInvoiceValue(stats: UserInvoiceStatistics[]): number {
    const totalSum = stats.reduce((sum, stat) => sum + (stat.sum ?? 0), 0);
    const totalCount = stats.reduce((count, stat) => count + stat.count, 0);
    return totalCount > 0 ? totalSum / totalCount : 0;
  }
}

export default StatisticsModel; 